import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntApp } from 'antd';
import { useTranslation } from 'react-i18next';

import { useStore } from '@/shared/store/app';
import { useAllowMeCollect } from '@/shared/hooks/useAllowMeCollect';
import {
  PrivateRoute,
  PublicRoute,
  LogoutQueryHandler,
  AccessManagementRoute,
} from '@/shared/components/router';
import { AuthLayout } from '@/shared/layouts';
import HomeLayout from '@/shared/layouts/HomeLayout';
import {
  AuthPage,
  ForgotPasswordPage,
  CreatePasswordPage,
  ResetPasswordPage,
  JoinPage,
  FederatedLoginPage,
  SsoCallbackPage,
} from './features/auth/components/ui/pages';
import { HomeContainer } from '@/features/home-v2/components/ui/pages';
import { UserManagementPage } from '@/features/users/components/ui/pages';
import heroImage from '@/assets/images/hero-pt.png';
import logo from '@/assets/images/logo.png';
import { isSsoFederationAllowed } from '@/shared/config/featureFlags';

import '@/styles/globals.css';
import './shared/i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { antdTheme } = useStore();
  const { t } = useTranslation();
  useAllowMeCollect();

  return (
    <ConfigProvider theme={antdTheme}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <Router>
            <LogoutQueryHandler>
              <Routes>
                {/* Private Routes - Require Authentication */}
                <Route element={<PrivateRoute />}>
                  <Route path="/" element={<HomeLayout />}>
                    <Route index element={<HomeContainer />} />
                    {/* RN09 — Gestão de acessos não existe para cliente
                        federado: a administração de usuários é do IdP dele. */}
                    <Route element={<AccessManagementRoute />}>
                      <Route
                        path="user-management"
                        element={<UserManagementPage />}
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Public Routes - Redirect if Authenticated */}
                <Route element={<PublicRoute />}>
                  <Route
                    path="/login"
                    element={
                      <AuthLayout
                        logo={
                          <Link to="/">
                            <img src={logo} alt="Logo" style={{ height: 40 }} />
                          </Link>
                        }
                        image={{
                          src: heroImage,
                          alt: 'Hero image',
                        }}
                        contentSpan={{ xs: 24, sm: 24, md: 24, lg: 10, xl: 14 }}
                        imageSpan={{ xs: 0, sm: 0, md: 0, lg: 14, xl: 10 }}
                      />
                    }
                  >
                    <Route index element={<AuthPage />} />
                  </Route>

                  <Route
                    path="/forgot-password"
                    element={
                      <AuthLayout
                        logo={
                          <Link to="/">
                            <img src={logo} alt="Logo" style={{ height: 40 }} />
                          </Link>
                        }
                        image={{
                          src: heroImage,
                          alt: 'Hero image',
                        }}
                        contentSpan={{ xs: 24, sm: 24, md: 24, lg: 10, xl: 14 }}
                        imageSpan={{ xs: 0, sm: 0, md: 0, lg: 14, xl: 10 }}
                      />
                    }
                  >
                    <Route index element={<ForgotPasswordPage />} />
                  </Route>

                  <Route
                    path="/reset-password"
                    element={
                      <AuthLayout
                        logo={
                          <Link to="/">
                            <img src={logo} alt="Logo" style={{ height: 40 }} />
                          </Link>
                        }
                        image={{
                          src: heroImage,
                          alt: 'Hero image',
                        }}
                        contentSpan={{ xs: 24, sm: 24, md: 24, lg: 10, xl: 14 }}
                        imageSpan={{ xs: 0, sm: 0, md: 0, lg: 14, xl: 10 }}
                      />
                    }
                  >
                    <Route index element={<ResetPasswordPage />} />
                  </Route>

                  {isSsoFederationAllowed() && (
                    <Route
                      path="/sso"
                      element={
                        <AuthLayout
                          logo={
                            <Link to="/">
                              <img
                                src={logo}
                                alt="Logo"
                                style={{ height: 40 }}
                              />
                            </Link>
                          }
                          image={{
                            src: heroImage,
                            alt: 'Hero image',
                          }}
                          contentSpan={{
                            xs: 24,
                            sm: 24,
                            md: 24,
                            lg: 10,
                            xl: 14,
                          }}
                          imageSpan={{ xs: 0, sm: 0, md: 0, lg: 14, xl: 10 }}
                        />
                      }
                    >
                      <Route index element={<FederatedLoginPage />} />
                    </Route>
                  )}

                  <Route
                    path="/create-password"
                    element={
                      <AuthLayout
                        logo={
                          <Link to="/">
                            <img src={logo} alt="Logo" style={{ height: 40 }} />
                          </Link>
                        }
                        image={{
                          src: heroImage,
                          alt: 'Hero image',
                        }}
                        contentSpan={{ xs: 24, sm: 24, md: 24, lg: 10, xl: 14 }}
                        imageSpan={{ xs: 0, sm: 0, md: 0, lg: 14, xl: 10 }}
                      />
                    }
                  >
                    <Route index element={<CreatePasswordPage />} />
                  </Route>
                </Route>

                {/* Hybrid Route - Federated SSO callback (minitoken exchange).
                  Outside both guards so neither bounces the browser away
                  before the token swap completes. */}
                {isSsoFederationAllowed() && (
                  <Route
                    path="/sso/callback"
                    element={
                      <AuthLayout
                        logo={
                          <Link to="/">
                            <img src={logo} alt="Logo" style={{ height: 40 }} />
                          </Link>
                        }
                        image={{
                          src: heroImage,
                          alt: 'Hero image',
                        }}
                        contentSpan={{ xs: 24, sm: 24, md: 24, lg: 10, xl: 14 }}
                        imageSpan={{ xs: 0, sm: 0, md: 0, lg: 14, xl: 10 }}
                      />
                    }
                  >
                    <Route index element={<SsoCallbackPage />} />
                  </Route>
                )}

                {/* Hybrid Route - Works with or without authentication */}
                <Route
                  path="/join"
                  element={
                    <AuthLayout
                      logo={
                        <Link to="/">
                          <img src={logo} alt="Logo" style={{ height: 40 }} />
                        </Link>
                      }
                      image={{
                        src: heroImage,
                        alt: 'Hero image',
                      }}
                      contentSpan={{ xs: 24, sm: 24, md: 24, lg: 10, xl: 14 }}
                      imageSpan={{ xs: 0, sm: 0, md: 0, lg: 14, xl: 10 }}
                    />
                  }
                >
                  <Route index element={<JoinPage />} />
                </Route>

                <Route
                  path="*"
                  element={<div>{t('404 - Page Not Found')}</div>}
                />
              </Routes>
            </LogoutQueryHandler>
          </Router>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
